import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Link,
  IconButton,
  TextField,
  Button,
  useTheme,
  InputAdornment,
  alpha,
} from "@mui/material";
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Email,
  LocationOn,
  Send,
  KeyboardArrowRight,
  PedalBike,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { NavLink } from "react-router-dom";

// --- IMAGES ---
const footerBgImages = [
  "https://images.unsplash.com/photo-1626767652471-209353793748?q=80&w=2070&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1558980394-4c7c9299fe96?q=80&w=2070&auto=format&fit=crop",
];

// --- STYLED COMPONENTS ---

// Background Slider
const SliderImage = styled(Box)(({ active }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundSize: "cover",
  backgroundPosition: "center",
  transition: "opacity 1.5s ease-in-out, transform 8s linear",
  opacity: active ? 1 : 0,
  transform: active ? "scale(1.05)" : "scale(1)",
  zIndex: -2,
}));

// Compact Glass Container
const GlassContainer = styled(Container)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  backgroundColor: alpha(theme.palette.blue_prime || "#044c7a", 0.85), // Slightly more opaque for cleaner look
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  // Reduced vertical padding for "Short Height"
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(3),
}));

const NewsletterInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: alpha(theme.palette.common.white, 0.1),
    borderRadius: "6px", // Smaller radius
    color: "#fff",
    fontSize: "0.875rem",
    height: "45px", // Compact height
    transition: "all 0.3s ease",
    border: "1px solid rgba(255,255,255,0.2)",
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.15),
      border: `1px solid ${theme.palette.prime || "#c85432"}`,
    },
    "&.Mui-focused": {
      backgroundColor: alpha(theme.palette.common.white, 0.2),
      border: `1px solid ${theme.palette.prime || "#c85432"}`,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& input::placeholder": { color: alpha(theme.palette.common.white, 0.6) },
}));

const SubscribeButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.prime || "#c85432",
  color: "#fff",
  fontWeight: 700,
  height: "45px", // Match input height
  borderRadius: "6px",
  textTransform: "none",
  boxShadow: `0 4px 14px ${alpha(theme.palette.prime || "#c85432", 0.4)}`,
  "&:hover": {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.prime || "#c85432",
  },
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.common.white, 0.08),
  color: "#fff",
  padding: "6px", // Smaller buttons
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.prime || "#c85432",
    transform: "translateY(-2px)",
  },
}));

const footerLinks = [
  { label: "Service", path: "/services" },
  { label: "Careers", path: "/careers" },
  { label: "About", path: "/about" },
];

export default function Footer() {
  const theme = useTheme();
  const primaryColor = theme.palette.prime || "#c85432";
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % footerBgImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        mt: 8,
        overflow: "hidden",
        display: "flex",
        alignItems: "flex-end",
      }}
    >
      {/* Slider Background */}
      {footerBgImages.map((img, index) => (
        <SliderImage
          key={index}
          active={index === currentSlide ? 1 : 0}
          sx={{ backgroundImage: `url(${img})` }}
        />
      ))}

      {/* Dark Overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: theme.palette.blue_prime || "#044c7a",
          opacity: 0.7,
          zIndex: -1,
        }}
      />

      <GlassContainer maxWidth="lg">
        <Grid container spacing={4} alignItems="flex-start">
          {/* 1. LEFT: Brand & Socials */}
          <Grid item xs={12} md={3}>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PedalBike sx={{ color: primaryColor, fontSize: 24 }} />
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{
                    letterSpacing: "0.5px",
                    background: `linear-gradient(45deg, #ffffff, ${primaryColor})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  SHAASHWAT
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  color: alpha("#fff", 0.7),
                  fontSize: "0.85rem",
                  lineHeight: 1.5,
                }}
              >
                Eternal mobility solutions. Building trusted journeys for the
                future.
              </Typography>
              <Stack direction="row" spacing={1}>
                {[Facebook, Twitter, Instagram, LinkedIn].map((Icon, i) => (
                  <SocialButton key={i} size="small">
                    <Icon fontSize="small" />
                  </SocialButton>
                ))}
              </Stack>
            </Stack>
          </Grid>

          {/* 2. MIDDLE: Compact Links & Contact (Merged for Space) */}
          <Grid item xs={12} md={5}>
            <Grid container spacing={2}>
              {/* Quick Links */}
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: "#fff", mb: 1.5 }}
                >
                  Quick Links
                </Typography>
                <Stack spacing={0.5}>
                  {footerLinks.map((link) => (
                    <Link
                      key={link.label}
                      component={NavLink}
                      to={link.path}
                      underline="none"
                      sx={{
                        color: alpha("#fff", 0.6),
                        fontSize: "0.85rem",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        "&:hover": { color: primaryColor },
                      }}
                    >
                      <KeyboardArrowRight
                        sx={{ fontSize: 14, color: primaryColor }}
                      />
                      {link.label}
                    </Link>
                  ))}
                </Stack>
              </Grid>

              {/* Contact */}
              <Grid item xs={6}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ color: "#fff", mb: 1.5 }}
                >
                  Contact
                </Typography>
                <Stack spacing={1.5}>
                  <Box display="flex" gap={1}>
                    <LocationOn sx={{ color: primaryColor, fontSize: 18 }} />
                    <Typography variant="caption" color={alpha("#fff", 0.7)}>
                      Surat, India
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <Email sx={{ color: primaryColor, fontSize: 18 }} />
                    <Typography variant="caption" color={alpha("#fff", 0.7)}>
                      info@shaashwat.com
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Grid>

          {/* 3. RIGHT: Newsletter (Clean Box) */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                bgcolor: alpha("#fff", 0.05),
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${alpha("#fff", 0.08)}`,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: "#fff", mb: 0.5 }}
              >
                Stay Updated
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: alpha("#fff", 0.6), display: "block", mb: 2 }}
              >
                Get the latest tracking news.
              </Typography>

              <Stack direction="row" spacing={1}>
                <NewsletterInput
                  placeholder="Email"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email
                          sx={{ color: alpha("#fff", 0.5), fontSize: 18 }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                <SubscribeButton sx={{ minWidth: "100px" }}>
                  Join
                </SubscribeButton>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        {/* --- BOTTOM CLEAN COPYRIGHT --- */}
        <Box
          sx={{
            borderTop: `1px solid ${alpha("#fff", 0.1)}`,
            mt: 4, // Reduced margin
            pt: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: alpha("#fff", 0.5) }}>
            © {new Date().getFullYear()} Shaashwat. All rights reserved.
          </Typography>

          <Stack direction="row" spacing={2}>
            {["Privacy", "Terms", "Support"].map((text) => (
              <Link
                key={text}
                href="#"
                underline="none"
                sx={{
                  color: alpha("#fff", 0.5),
                  fontSize: "0.75rem",
                  "&:hover": { color: primaryColor },
                }}
              >
                {text}
              </Link>
            ))}
          </Stack>
        </Box>
      </GlassContainer>
    </Box>
  );
}
